import { SchedulingType } from "../enums/taskType";
import { Asset, AssetManualMetrics } from "../models/asset";
import { Dealership } from "../models/dealership";
import { Scheduling } from "../models/schedule";
import { getGroupIdByName } from "../services/groupService";
import { extractContractModality } from "../utils/contractUtils";
import { toUtcDateString } from "../utils/dateUtils";
import { AssetPlanDto } from "./assetPlanDto";
import { CheckupDto } from "./checkupDto";

const getSchedulingMetricDate = (data: any): Date | undefined => {
  const dates: Date[] = [];

  if (data.maintenance?.checkoutDate) {
    dates.push(new Date(toUtcDateString(data.maintenance.checkoutDate)));
  }
  if (data.schedule?.scheduledDate) {
    dates.push(new Date(toUtcDateString(data.schedule.scheduledDate)));
  }

  if (!dates.length) return undefined;

  return dates.reduce((latest, current) =>
    current.getTime() > latest.getTime() ? current : latest
  );
};

const resolveScheduleHourMeter = (data: any): number | undefined => {
  const value =
    data.schedule?.hourMeter ??
    data.schedule?.hourmeter ??
    data.maintenance?.hourmeter ??
    data.maintenance?.hourMeter;

  return (value ?? 0) > 0 ? Number(value) : undefined;
};

export const AssetDto = (data: any): Asset => {
  const volkstotalFromRoot = data.volkstotal ?? data.VolksTotal ?? data.volksTotal;
  const contractNumberFromRoot = volkstotalFromRoot?.contractNumber ?? volkstotalFromRoot?.contractnumber ?? volkstotalFromRoot?.contract;
  const contractNumberFromVehicle = data.vehicle?.contractNumber ?? data.vehicle?.contractnumber ?? data.vehicle?.contract;
  const contractModalityFromPayload = extractContractModality({ ...data, volkstotal: volkstotalFromRoot });

  const result = {
    id: data.vehicle.assetId,
    name: data.vehicle.name,
    model: data.vehicle.vehicleModel ?? data.planInfo?.planVehicleDetails?.modelName,
    chassis: data.vehicle.chassis ?? data.planInfo?.planVehicleDetails?.chassis,
    odometer: data.vehicle.odometer,
    lastOdometerUpdate: data.vehicle.metricDates?.odometer ? new Date(toUtcDateString(data.vehicle.metricDates?.odometer)) : undefined,
    hourmeter: data.vehicle.hourMeter,
    lastHourmeterUpdate: data.vehicle.metricDates?.hourMeter ? new Date(toUtcDateString(data.vehicle.metricDates?.hourMeter)) : undefined,
    year: data.vehicle.vehicleYear,
    licensePlate: (data.vehicle.plate ?? data.vehicle.licensePlate ?? data.planInfo?.planVehicleDetails?.licensePlate) !== '-' ? (data.vehicle.plate ?? data.vehicle.licensePlate ?? data.planInfo?.planVehicleDetails?.licensePlate) : null,
    plans: (data.plans || data.vehiclePlan)?.map(AssetPlanDto),
    volksTotalPlan: data.volksTotal ?? volkstotalFromRoot?.modality ?? volkstotalFromRoot?.modalityName,
    contractNumber: contractNumberFromRoot ?? contractNumberFromVehicle,
    contractModality: contractModalityFromPayload ?? undefined,
    score: data.vehicleScore ?? "-",
    groupName: data.vehicle.vehicleGroup,
    group: data.vehicle.vehicleGroupId ?? getGroupIdByName(data.vehicle.vehicleGroup),
    groupIds: data.groups,
    warrantyStatus: data.warrantyStatus,
    status: data.status,
    geoLocation: data.geoLocation,
    emissionStandard: data.vehicle?.emissionStandard ?? data.vehicle?.emissionStandart,
    campaigns: data.campaigns,
    accountName: data.accountName ?? data.planInfo?.planVehicleDetails?.accountName,
    schedulings: {
      previous: data.previousCheckups
        ?.filter(Boolean)
        ?.map((checkup: any) => AssetSchedulingDto({ ...checkup, isFinished: true })),
      next: data.nextCheckups
        ?.filter(Boolean)
        ?.map((checkup: any) => AssetSchedulingDto({ ...checkup, isFinished: false })),
    },
    hasTruckerPayPlan: data.planInfo?.hasTruckerPayPlan ?? false,
    hasVolksEssentialsPlan: data.planInfo?.hasVolksEssentialsPlan ?? false,
    planInfo: data.planInfo,
  };
  
  return result;
};

export const AssetSchedulingDto = (data: any): Scheduling => {
  // Criar um asset básico com os dados necessários
  const basicAsset = {
    id: data.vehicle?.assetId || '',
    name: data.vehicle?.name || '',
    model: data.vehicle?.vehicleModel || '',
    chassis: data.vehicle?.chassis || '',
    odometer: data.vehicle?.odometer || 0,
    hourmeter: data.vehicle?.hourMeter || 0,
    year: data.vehicle?.vehicleYear || 0,
    // Compatibilidade: algumas rotas retornam `plate`, outras `licensePlate`
    licensePlate: (data.vehicle?.plate ?? data.vehicle?.licensePlate) !== '-' ? (data.vehicle?.plate ?? data.vehicle?.licensePlate) : null,
    groupName: data.vehicle?.vehicleGroup || '',
    group: data.vehicle?.vehicleGroupId || '',
  };
  
  return {
    id: data.id ?? data.schedule?.id,
    scheduleNumber: data.scheduleNumber,
    checkup: CheckupDto({
      odometer: data.checkup?.range?.start ?? data.schedule?.odometer ?? null,
      hourMeter: resolveScheduleHourMeter(data) ?? null,
      type: SchedulingType.PREVENTIVA,
      range: data?.checkup?.range ?? { 
        start: data.schedule?.odometer, end: null 
      },
      checkupType: data.checkup?.checkupType ?? data.maintenance?.checkupType,
    }),
    hourmeter: resolveScheduleHourMeter(data),
    odometer: (() => {
      const c = data.checkup?.range?.start ?? 0;
      const s = data.schedule?.odometer ?? 0;
      if (Number.isFinite(c) && c > 0) return c;
      if (Number.isFinite(s) && s > 0) return s;
      return undefined;
    })(),
    asset: basicAsset,
    driver: undefined,
    date: getSchedulingMetricDate(data) ?? new Date(0),
    os: data.maintenance?.serviceOrder,
    isFinished: data.isFinished ?? false,
    maintenanceStatus: data.maintenance?.statusId,
    dealership: {
      id: data.maintenance?.dealershipId ?? '',
      name: data.maintenance?.dealershipName ?? '',
      address: data.maintenance?.dealershipAddress ?? '',
    },
    maintenanceData: {
      volksEssentialsPlanContract: data.maintenance?.volksEssentialsPlanContract ?? null,
      odometer: data.maintenance?.odometer ?? null,
      hourmeter: data.maintenance?.hourmeter ?? null,
      checkupType: data.maintenance?.checkupType ?? null,
      consultant: data.maintenance?.consultantName ?? null,
    }
  };
}

export const AssetSummaryDto = (data: any): Asset => {
  return {
    id: data.id,
    name: data.name,
    type: data.type,
    groupIds: data.groupIds?.length ? data.groupIds : ['ungruped'],
    chassis: data.identification,
    odometer: 0,
    hourmeter: 0,
    model: '',
    year: 0,
    licensePlate: data.license_plate,
  };
};

export const UptimeAssetDto = (data: any): AssetManualMetrics => {
  return {
    assetId: data.assetId,
    odometer: data.odometer,
    hourmeter: data.hourmeter,
    lastOdometerUpdate: data.lastOdometerUpdate
      ? new Date(data.lastOdometerUpdate)
      : undefined,
    lastHourmeterUpdate: data.lastHourmeterUpdate
      ? new Date(data.lastHourmeterUpdate)
      : undefined
  };
};
